/**
 * Geometria pura de "Ao longo da apuração" (§7.13): pontos do histórico cortados no instante exibido (replay), eixo X
 * pela fração das seções, eixo Y com zoom automático, traço pontilhado de 1% a 5% e sólido a partir de 5%, e o ponto de
 * cruzamento da última mudança de liderança nacional. As duas séries usam a MESMA regra (N03): trocar 13 e 22 nos
 * dados só troca as séries entre si. Coordenadas em px da área medida (o `viewBox` do SVG).
 */
import { t } from '../../copy/strings';
import { fmtInt, fmtPctAdaptive, fmtSections } from '../../format/numbers';
import { tMinAt } from '../../format/time';
import type { Candidate, ElectionEvent, HistoryPoint } from '../../model/types';

/** Pontos com menos de 1% das seções não são desenhados (§7.13 bloco 3). */
export const MIN_FRACTION = 0.01;
/** Até 5% o traço é pontilhado (o começo engana). */
export const SOLID_FRACTION = 0.05;
/** O SVG só é desenhado com a área a partir desta altura. */
export const MIN_PLOT_H = 36;
/** Com eixos a partir desta altura (rótulos X e grade com rótulos Y). */
export const AXES_MIN_H = 84;
/** Passo de 4 pontos abaixo desta altura; senão 2. */
export const FINE_STEP_MIN_H = 120;

/** Ponto desenhável: fração das seções e as fatias [A, B] dos válidos. */
export interface TrendPoint {
  seq: number;
  tMin: number;
  f: number;
  shares: [number, number];
}

/** Pontos com `t_min ≤` instante exibido e ≥ 1% das seções, na ordem de `seq` (§7.13 bloco 3; AC-7.13-05). */
export function trendPoints(points: readonly HistoryPoint[], instantTMin: number): TrendPoint[] {
  const out: TrendPoint[] = [];
  for (const p of points) {
    if (!(p.t_min <= instantTMin)) continue;
    if (!(p.sections_fraction >= MIN_FRACTION) || !(p.valid_votes > 0)) continue;
    out.push({
      seq: p.seq,
      tMin: p.t_min,
      f: Math.min(1, p.sections_fraction),
      shares: [p.votes[0] / p.valid_votes, p.votes[1] / p.valid_votes],
    });
  }
  return out;
}

/** Curva só com 2 pontos ou mais (≥ 1%); senão o estado vazio "A curva começa com 1% das seções" (AC-7.13-01). */
export const hasCurve = (pts: readonly TrendPoint[]): boolean => pts.length >= 2;

export interface TrendFrame {
  w: number;
  h: number;
  /** Rótulos X e grade com rótulos Y (altura ≥ 84). */
  axes: boolean;
  ml: number;
  mr: number;
  mt: number;
  mb: number;
  /** Faixa do eixo Y em pontos percentuais (inteiros, múltiplos do passo). */
  yMin: number;
  yMax: number;
  /** 4 ou 2 pontos. */
  step: number;
}

/**
 * Margens, passo e zoom do eixo Y (§7.13 bloco 2): pontos de referência ≥ 5% (ou ≥ 1% se ainda não houver);
 * `min = floor((menor − 1) / passo) × passo`, `max = ceil((maior + 1) / passo) × passo`, `max ≥ min + 2 × passo`. As
 * contas em pontos percentuais inteiros (evitam 0,4599…).
 */
export function trendFrame(pts: readonly TrendPoint[], w: number, h: number): TrendFrame {
  const axes = h >= AXES_MIN_H;
  const step = h < FINE_STEP_MIN_H ? 4 : 2;
  const solid = pts.filter((p) => p.f >= SOLID_FRACTION);
  const ref = solid.length > 0 ? solid : pts;
  let lo = 50;
  let hi = 50;
  if (ref.length > 0) {
    lo = Number.POSITIVE_INFINITY;
    hi = Number.NEGATIVE_INFINITY;
    for (const p of ref) {
      for (const s of p.shares) {
        lo = Math.min(lo, s * 100);
        hi = Math.max(hi, s * 100);
      }
    }
  }
  const clean = (x: number) => Number(x.toPrecision(12));
  let yMin = Math.max(0, Math.floor(clean((lo - 1) / step)) * step);
  let yMax = Math.min(100, Math.ceil(clean((hi + 1) / step)) * step);
  if (yMax < yMin + 2 * step) yMax = yMin + 2 * step;
  if (yMax > 100) {
    yMax = 100;
    yMin = Math.min(yMin, 100 - 2 * step);
  }
  return axes
    ? { w, h, axes, ml: 34, mr: 6, mt: 6, mb: 18, yMin, yMax, step }
    : { w, h, axes, ml: 6, mr: 6, mt: 6, mb: 6, yMin, yMax, step };
}

const r2 = (x: number): number => Math.round(x * 100) / 100;

/** X da fração das seções (0 a 1, linear). */
export function xOf(fr: TrendFrame, f: number): number {
  return r2(fr.ml + f * (fr.w - fr.ml - fr.mr));
}

/** Y de uma fatia (0 a 1). */
export function yOf(fr: TrendFrame, share: number): number {
  const span = fr.yMax - fr.yMin;
  return r2(fr.mt + ((fr.yMax - share * 100) / span) * (fr.h - fr.mt - fr.mb));
}

function pathOf(fr: TrendFrame, pts: readonly TrendPoint[], side: 0 | 1): string {
  return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${xOf(fr, p.f)},${yOf(fr, p.shares[side])}`).join('');
}

export interface TrendSeries {
  side: 0 | 1;
  /** Traço pontilhado de 1% até o 1º ponto ≥ 5% (inclusive, para emendar); null sem trecho < 5%. */
  dotted: string | null;
  /** Traço sólido a partir do 1º ponto ≥ 5% (até o penúltimo, quando o último é o trecho novo); null com < 2 pontos. */
  solid: string | null;
  /** Trecho novo ao vivo (do ponto anterior ao novo), desenhado por `stroke-dashoffset` (A14); null sem trecho novo. */
  fresh: string | null;
  /** Ponto final (círculo de raio 3,5). */
  end: { x: number; y: number };
}

/**
 * Uma série (§7.13 bloco 2; AC-7.13-02): com pontos em 2%, 4%, 6% e 10%, pontilhado de 2% a 6% e sólido de 6% a 10%.
 * `fresh`: o último ponto chegou ao vivo agora e o trecho que leva a ele é sólido (o pontilhado aparece sem desenho).
 */
export function trendSeries(fr: TrendFrame, pts: readonly TrendPoint[], side: 0 | 1, fresh = false): TrendSeries {
  const n = pts.length;
  const firstSolid = pts.findIndex((p) => p.f >= SOLID_FRACTION);
  const dottedPts = firstSolid === -1 ? pts : pts.slice(0, firstSolid + 1);
  const solidPts = firstSolid === -1 ? [] : pts.slice(firstSolid);
  const freshOk = fresh && solidPts.length >= 2;
  const solidMain = freshOk ? solidPts.slice(0, -1) : solidPts;
  const last = pts[n - 1] as TrendPoint;
  return {
    side,
    dotted: dottedPts.length >= 2 ? pathOf(fr, dottedPts, side) : null,
    solid: solidMain.length >= 2 ? pathOf(fr, solidMain, side) : null,
    fresh: freshOk ? pathOf(fr, solidPts.slice(-2), side) : null,
    end: { x: xOf(fr, last.f), y: yOf(fr, last.shares[side]) },
  };
}

/** Linhas da grade (a cada passo, de `yMin` a `yMax`) com o rótulo "46%". */
export function trendGrid(fr: TrendFrame): Array<{ y: number; label: string }> {
  const out: Array<{ y: number; label: string }> = [];
  for (let v = fr.yMin; v <= fr.yMax; v += fr.step) out.push({ y: yOf(fr, v / 100), label: `${fmtInt(v)}%` });
  return out;
}

/** Última mudança de liderança nacional com `occurred_at ≤` instante exibido (§7.13 bloco 3), ou null. */
export function lastLeadChange(events: readonly ElectionEvent[], instantTMin: number): ElectionEvent | null {
  let best: ElectionEvent | null = null;
  let bestT = Number.NEGATIVE_INFINITY;
  for (const e of events) {
    if (e.type !== 'national_lead_change') continue;
    const at = tMinAt(Date.parse(e.occurred_at));
    if (!(at <= instantTMin)) continue;
    if (at >= bestT) {
      best = e;
      bestT = at;
    }
  }
  return best;
}

/**
 * Ponto de cruzamento (§7.13 bloco 3): o `crossing` do evento, se houver; senão interpolação linear entre os dois
 * pontos consecutivos em que o líder troca (`s = d0 / (d0 − d1)`, `d = fatiaA − fatiaB`), com y = 50% no 2º turno
 * (AC-7.13-04). Usa a ÚLTIMA troca entre os pontos desenhados; null sem troca.
 */
export function crossingPoint(
  pts: readonly TrendPoint[],
  event: ElectionEvent | null,
): { f: number; share: number } | null {
  if (event === null) return null;
  const d = event.data;
  if (d.type === 'national_lead_change' && d.crossing) {
    return { f: d.crossing.sections_fraction, share: d.crossing.share_to };
  }
  for (let i = pts.length - 1; i >= 1; i--) {
    const p0 = pts[i - 1] as TrendPoint;
    const p1 = pts[i] as TrendPoint;
    const d0 = p0.shares[0] - p0.shares[1];
    const d1 = p1.shares[0] - p1.shares[1];
    if (d0 * d1 < 0) {
      const s = d0 / (d0 - d1);
      return { f: p0.f + s * (p1.f - p0.f), share: 0.5 };
    }
  }
  return null;
}

/** `trend.aria` (§7.13 bloco 8; §15.4.2): números do último ponto desenhado, ordem 13 | 22; + a mudança de liderança. */
export function trendAria(
  candidates: readonly [Pick<Candidate, 'display_name'>, Pick<Candidate, 'display_name'>],
  pts: readonly TrendPoint[],
  crossing: { f: number } | null,
): string {
  const last = pts[pts.length - 1] as TrendPoint;
  const [a, b] = last.shares;
  const base = t('trend.aria', {
    S: fmtSections(last.f),
    A: candidates[0].display_name,
    pA: fmtPctAdaptive(a, a, b),
    B: candidates[1].display_name,
    pB: fmtPctAdaptive(b, a, b),
  });
  return crossing ? base + t('trend.aria.leadChange', { P1: fmtSections(crossing.f) }) : base;
}
